import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  Divider,
  Autocomplete,
  Checkbox,
  Box } from
'@mui/material';
import { COUNTRIES, TAX_TYPES, TAX_SUB_TYPES, LEGAL_TYPES, CURRENCIES } from '../../constants/dropdownOptions';

const EntityDialog = ({ open, onClose, onCancel, onSave, entity, editMode, pointInTimes = [] }) => {
  const [currentEntity, setCurrentEntity] = React.useState(entity || {});

  // Use shared constants for dropdown options
  const taxTypes = TAX_TYPES;
  const taxSubTypes = TAX_SUB_TYPES;
  const legalTypes = LEGAL_TYPES;

  // Update currentEntity when entity prop changes
  React.useEffect(() => {
    setCurrentEntity(entity || {});
  }, [entity]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentEntity((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    // Ensure all required fields are properly set before saving

    onSave(currentEntity);
  };

  const handleTaxYearEndChange = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove all non-digits

    // Format as MM/DD/YYYY
    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5, 9);
    }

    setCurrentEntity((prev) => ({
      ...prev,
      taxYearEnd: value
    }));
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 10 }, (_, i) => ({
      value: `${currentYear - i}-12-31`,
      label: `${currentYear - i} December 31`
    }));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>{editMode ? 'Edit Entity' : 'Add New Entity'}</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {/* Only include the 8 fields shown in the table */}
                <Grid item xs={12} sm={6}>
                  <TextField
              name="code"
              label="Entity Code"
              fullWidth
              value={currentEntity.code}
              onChange={handleInputChange}
              required
              disabled={editMode}
              autoComplete="off" />

                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
              name="name"
              label="Entity Name"
              fullWidth
              value={currentEntity.name}
              onChange={handleInputChange}
              required
              autoComplete="off" />

                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Tax Type</InputLabel>
                    <Select
                name="taxType"
                value={currentEntity.taxType || ''}
                onChange={handleInputChange}
                label="Tax Type">

                      {taxTypes.map((type) =>
                <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                      <InputLabel>Tax Subtype</InputLabel>
                      <Select
                name="taxSubType"
                value={currentEntity.taxSubType || ''}
                onChange={handleInputChange}
                label="Tax Subtype">

                        {taxSubTypes.map((type) =>
                <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                )}
                      </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Legal Type</InputLabel>
                    <Select
                name="legalType"
                value={currentEntity.legalType || ''}
                onChange={handleInputChange}
                label="Legal Type">

                      {legalTypes.map((type) =>
                <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
              name="region"
              label="Region"
              fullWidth
              value={currentEntity.region || ''}
              onChange={handleInputChange}
              required
              autoComplete="off" />

                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
              options={COUNTRIES}
              value={currentEntity.country || ''}
              onChange={(event, newValue) => {
                setCurrentEntity({
                  ...currentEntity,
                  country: newValue
                });
              }}
              renderInput={(params) =>
              <TextField {...params} label="Country" fullWidth autoComplete="off" />
              } />

                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                name="active"
                value={currentEntity.active !== undefined ? currentEntity.active : true}
                onChange={handleInputChange}
                label="Status"
                required>

                      <MenuItem value={true}>Active</MenuItem>
                      <MenuItem value={false}>Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Currency</InputLabel>
                    <Select
                name="currency"
                value={currentEntity.currency || ''}
                onChange={handleInputChange}
                label="Currency"
                required>

                      {CURRENCIES.map((curr) =>
                <MenuItem key={curr.code} value={curr.code}>
                          {curr.code}
                        </MenuItem>
                )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
              select
              name="PointInTime"
              label="Point In Time"
              fullWidth
              value={currentEntity.PointInTime || ''}
              onChange={handleInputChange}
              required>

                    {pointInTimes.map((pit) =>
              <MenuItem key={pit.id} value={pit.Code}>
                        {pit.Name}
                      </MenuItem>
              )}
                  </TextField>
                </Grid>
                {/* End of required fields*/}
                <Grid item xs={12}>
                  <Divider style={{ marginTop: '1rem', marginBottom: '1rem' }} />
                </Grid>
    
                <Grid item xs={12} sm={6}>
                  <TextField
              name="entityShortName"
              label="Entity Short Name"
              fullWidth
              value={currentEntity.entityShortName || ''}
              onChange={handleInputChange}
              autoComplete="off" />

                </Grid>
    
                <Grid item xs={12} sm={6}>
                  <TextField
              name="segment"
              label="Segment"
              fullWidth
              value={currentEntity.segment || ''}
              onChange={handleInputChange}
              autoComplete="off" />

                </Grid>
    
                <Grid item xs={12} sm={6}>
                  <TextField
              name="taxId"
              label="Tax ID"
              fullWidth
              value={currentEntity.taxId || ''}
              onChange={handleInputChange}
              autoComplete="off" />

                </Grid>
    
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Reporting Basis</InputLabel>
                    <Select
                name="reportingBasis"
                value={currentEntity.reportingBasis || ''}
                onChange={handleInputChange}
                label="Reporting Basis">

                      <MenuItem value="US GAAP">US GAAP</MenuItem>
                      <MenuItem value="IFRS">IFRS</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
    
                <Grid item xs={12} sm={6}>
                  <TextField
              name="taxYearEnd"
              label="Tax Year-End"
              fullWidth
              value={currentEntity.taxYearEnd || ''}
              onChange={handleTaxYearEndChange}
              placeholder="MM/DD/YYYY"
              helperText="Enter date in MM/DD/YYYY format"
              inputProps={{ maxLength: 10 }}
              autoComplete="off" />

                </Grid>
    
                <Grid item xs={12} sm={6}>
                  <TextField
              name="taxFilingEntityCode"
              label="Tax Filing Entity Code"
              fullWidth
              value={currentEntity.taxFilingEntityCode || ''}
              onChange={handleInputChange}
              autoComplete="off" />

                </Grid>
    
                <Grid item xs={12} sm={6}>
                  <TextField
              name="ownership"
              label="Ownership Percentage"
              fullWidth
              value={currentEntity.ownership || ''}
              onChange={handleInputChange}
              autoComplete="off" />

                </Grid>
    
                <Grid item xs={12} sm={6}>
                  <TextField
              name="countryOfInc"
              label="Country of Incorporation"
              fullWidth
              value={currentEntity.countryOfInc || ''}
              onChange={handleInputChange}
              autoComplete="off" />

                </Grid>
    
                <Grid item xs={12} sm={6}>
                  <TextField
              name="countryASU"
              label="Country ASU"
              fullWidth
              value={currentEntity.countryASU || ''}
              onChange={handleInputChange}
              autoComplete="off" />

                </Grid>
    
                <Grid item xs={12} sm={6}>
                  <TextField
              name="countryCbCR"
              label="Country CbCR"
              fullWidth
              value={currentEntity.countryCbCR || ''}
              onChange={handleInputChange}
              autoComplete="off" />

                </Grid>
    
                <Grid item xs={12} sm={6}>
                  <TextField
              name="countryOECD"
              label="Country OECD"
              fullWidth
              value={currentEntity.countryOECD || ''}
              onChange={handleInputChange}
              autoComplete="off" />

                </Grid>
                
                
                {/* Boolean fields as Material-UI checkboxes */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <FormControlLabel
                control={
                <Checkbox
                  checked={currentEntity.isTopSide || false}
                  onChange={(e) => setCurrentEntity({
                    ...currentEntity,
                    isTopSide: e.target.checked
                  })}
                  name="isTopSide" />

                }
                label="Is Top Side" />

                    
                    <FormControlLabel
                control={
                <Checkbox
                  checked={currentEntity.isTopSideUS || false}
                  onChange={(e) => setCurrentEntity({
                    ...currentEntity,
                    isTopSideUS: e.target.checked
                  })}
                  name="isTopSideUS" />

                }
                label="Is Top Side US" />

                    
                    <FormControlLabel
                control={
                <Checkbox
                  checked={currentEntity.isBranch || false}
                  onChange={(e) => setCurrentEntity({
                    ...currentEntity,
                    isBranch: e.target.checked
                  })}
                  name="isBranch" />

                }
                label="Is Branch" />

                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={onCancel || handleClose}>Cancel</Button>
              <Button onClick={handleSave} variant="contained" color="primary">
                Save
              </Button>
            </DialogActions>
          </Dialog>);

};

export default EntityDialog;
